import React from 'react';
import PropTypes from 'prop-types';
import { BackgroundColorContainer, Heading } from '@web/atoms';
import { Carousel, CtaButton, CtaShell } from '@web/molecules';
import { useReporter } from '../../reporter';
import { BeforeAfterTestimonial } from '../BeforeAfterTestimonial';
import { Testimonial } from './Testimonial';
import {
  TestimonialsContainer,
  ButtonContainer,
  CarouselContainer,
  SubHeadline,
  ContainerHeader,
} from './styles';
import { testimonialsButtonClicked, testimonialsCarouselTurn } from './events';

export const Testimonials = ({
  testimonials,
  headline,
  showStars,
  link,
  subheadline,
  backgroundColor = 'background.base',
  fontColor = 'neutral.dark',
  typography = 'heading1',
  level,
  id,
  starColor,
}) => {
  const reporter = useReporter();
  const onTestimonialsClicked = target => {
    reporter.tag(testimonialsCarouselTurn(target));
  };

  return (
    <BackgroundColorContainer $bgColor={backgroundColor}>
      <TestimonialsContainer id={id}>
        <ContainerHeader>
          {headline && (
            <Heading
              headingLevel={level}
              typography={typography}
              headingText={headline}
              backgroundColor={backgroundColor}
              fontColor={fontColor}
              position="center"
              verticalPadding={0}
              horizontalPadding={0}
            />
          )}
          {subheadline && <SubHeadline>{subheadline}</SubHeadline>}
        </ContainerHeader>

        <CarouselContainer>
          <Carousel
            buttonPosition={'45'}
            itemsToShow={4}
            onArrowClick={target => onTestimonialsClicked(target)}
            onDotClick={() => onTestimonialsClicked('dots')}
          >
            {testimonials.map((testimonial, i) =>
              testimonial.component === 'before_after_testimonial' ? (
                <BeforeAfterTestimonial key={i} {...testimonial} />
              ) : (
                <Testimonial
                  backgroundColor={
                    backgroundColor === 'background.base'
                      ? 'neutral.cream'
                      : 'background.base'
                  }
                  showStars={showStars}
                  starColor={starColor}
                  {...testimonial}
                  key={testimonial.name}
                />
              ),
            )}
          </Carousel>
        </CarouselContainer>
        {link && (
          <ButtonContainer>
            <CtaShell
              alignment={link.alignment}
              fullWidth={link.fullWidth}
              fullWidthMobile={link.fullWidthMobile}
              fullWidthDesktop={link.fullWidthDesktop}
            >
              <CtaButton
                {...link}
                href={link.target}
                variant="lilac"
                icon={link.icon}
                iconAlignment={link.iconAlignment}
                headerText={link.headerText}
                onClick={() =>
                  reporter.tag(
                    testimonialsButtonClicked(link.text, link.target),
                  )
                }
              >
                {link.text}
              </CtaButton>
            </CtaShell>
          </ButtonContainer>
        )}
      </TestimonialsContainer>
    </BackgroundColorContainer>
  );
};

Testimonials.displayName = 'Testimonials';

Testimonials.propTypes = {
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      component: PropTypes.string,
    }),
  ).isRequired,
  headline: PropTypes.string,
  link: PropTypes.shape({
    target: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }),
  showStars: PropTypes.bool,
  subheadline: PropTypes.string,
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  typography: PropTypes.string,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  id: PropTypes.string,
  starColor: PropTypes.string,
};
